import { CheckCircle, Clock } from 'lucide-react'
import { Button, toast } from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import type { IncidentReport } from '../api/types'
import { useUpdateIncident } from '../api/queries'

interface Props {
  report: IncidentReport
}

function _fmt(ts: string | null): string {
  if (!ts) return ''
  return new Date(ts).toLocaleString()
}

export function SignOffPanel({ report }: Props) {
  const update = useUpdateIncident(report.id)

  const canSign = report.status === 'submitted'

  async function sign() {
    try {
      await update.mutateAsync({ action: 'sign' })
      toast.success('Management sign-off recorded.')
    } catch (error) {
      toast.danger(error instanceof ApiError ? error.message : 'Sign-off failed. Please try again.')
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border-default p-4 space-y-3">
        <div className="flex items-center gap-2">
          {report.mgmt_signed_at ? (
            <CheckCircle size={16} className="text-success" />
          ) : (
            <Clock size={16} className="text-secondary" />
          )}
          <span className="text-sm font-medium">Management Sign Off</span>
          {report.mgmt_signed_at && (
            <span className="text-xs text-secondary ml-auto">{_fmt(report.mgmt_signed_at)}</span>
          )}
        </div>
        {report.mgmt_signed_at ? (
          <p className="text-sm text-secondary">
            Signed by {report.mgmt_signed_by_name ?? report.mgmt_signed_by_email ?? 'unknown'}
          </p>
        ) : canSign ? (
          <div className="flex justify-end">
            <Button variant="secondary" size="sm" onClick={sign} loading={update.isPending}>
              Sign
            </Button>
          </div>
        ) : (
          <p className="text-sm text-secondary">Submit the report to enable sign-off.</p>
        )}
      </div>

      {report.status === 'reviewed' && (
        <p className="text-xs text-success text-center">
          ✓ Report fully signed and reviewed
        </p>
      )}
    </div>
  )
}
